import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./compose-email.scss";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import AttachmentIcon from "../../../../../../../assets/icons/attatchment.svg";
import { emailValidationRegex } from "../../../../../../../constants/regexStrings";
import { AlertComponent } from "../../../../../../../common/components";
import { getIconBasedOnFileExt } from "../../../../../../../utils/fetchIconForExtension";
import { createEmail } from "../../../../../../../api/campaign";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";
import {
  replaceMergeFields,
  convertKeysToPascalCase,
  htmlToPlainText,
} from "../../../../../../leads/lead-view/lead-view-details/lead-view-details-view/lead-view-details-view-email-info/email-letter-helper";
import LexicalEditor from "../../../../../../../common/components/text-editor/text-editor";
import { isLexicalEditorEmpty } from "../../../../../../leads/lead-view/lead-view-details/lead-view-details-view/lead-view-details-view-email-info/email-letter-helper";
import { validateMergeTokens } from "../../../../../../surveys/email-letter-helper";
import { getEmailTemplateDetails } from "../../../../../../../api/email-letter-templates";

interface Props {
  open: boolean;
  onClose: () => void;
  customerDetails?: any;
  onSuccess?: () => void;
  templateData?: any;
  templateMetaData?: any;
  setSelectedTemplateData?: any;
}

interface EmailAttachment {
  name: string;
  type: string;
  base64: string;
  size?: number; // Size in bytes
}

interface QRCodeInsert {
  id: string;
  name: string;
  base64: string;
}

const isValidEmail = (email: string): boolean => {
  return emailValidationRegex.test(email.trim());
};

const ComposeEmailDialog: React.FC<Props> = ({
  open,
  onClose,
  customerDetails,
  onSuccess,
  templateData,
  templateMetaData,
  setSelectedTemplateData,
}) => {
  const [to, setTo] = useState<string[]>([
    customerDetails?.primary_email_address,
  ]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);

  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");

  const [toError, setToError] = useState("");
  const [ccError, setCcError] = useState("");
  const [bccError, setBccError] = useState("");

  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [qrCodeInserts, setQrCodeInserts] = useState<QRCodeInsert[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const [attachmentError, setAttachmentError] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [subjectError, setSubjectError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [placeHolderValues, setPlaceHolderValues] = useState<any>(null);
  const [defaulHtmlMessage, setDefaultHtmlMessage] = useState("");
  const [editorInstance, setEditorInstance] = useState<any>(null);

  const callValidateMergeTokens = (htmlString: string) => {
    setMessageError('')
    const isValidBody = validateMergeTokens(htmlString);
    if (!isValidBody) {
      setMessageError(
        `Email body contains invalid or incomplete merge tokens`
      );
    }
    return isValidBody;
  };

  useEffect(() => {
    if (customerDetails?.primary_email_address) {
      setTo([customerDetails.primary_email_address]);
    } else {
      setTo([]);
    }
  }, [customerDetails]);

  const [isCursorInBody, setIsCursorInBody] = useState(false);

  const handleReset = () => {
    if (customerDetails?.primary_email_address) {
      setTo([customerDetails.primary_email_address]);
    } else {
      setTo([]);
    }
    setCc([]);
    setBcc([]);
    setToInput("");
    setCcInput("");
    setBccInput("");
    setToError("");
    setCcError("");
    setBccError("");
    setSubject("");
    setSubjectError("");
    setMessageError("");
    setMessage("");
    setAttachments([]);
    setQrCodeInserts([]);
    setShowCc(false);
    setShowBcc(false);
    setIsCursorInBody(false);
    setDefaultHtmlMessage("");
  };

  // Handle message content change for content editable div
  const handleContentChange = () => {
    if (contentEditableRef.current) {
      const content = contentEditableRef.current.innerHTML;
      setMessage(content);
      if (messageError) setMessageError("");
    }
  };

  // Handle cursor position tracking for content editable
  const handleContentClick = () => {
    if (contentEditableRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        setIsCursorInBody(true);
      }
    }
  };

  const handleContentFocus = () => {
    setIsCursorInBody(true);
  };

  // Handle blur on content editable
  const handleContentBlur = () => {
    setIsCursorInBody(false);
  };

  // Handle QR Code insertion from common component
  const handleQRCodeInserted = (qrCode: QRCodeInsert) => {
    setQrCodeInserts((prev) => [...prev, qrCode]);
    // Update message content after QR code insertion
    handleContentChange();
    // Maintain cursor focus
    setIsCursorInBody(true);

    // Show success message
    setAlertMessage("QR code inserted successfully!");
    setAlertType("success");
    setAlertOpen(true);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files) return;

    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "image/png",
      "image/jpeg",
      "text/plain", // .txt
      "text/csv", // .csv
      "application/msword", // .doc
    ];

    const validFiles = Array.from(files).filter((file) =>
      allowedTypes.includes(file.type)
    );

    const convertToBase64 = (file: File): Promise<EmailAttachment> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(",")[1];
          resolve({
            name: file.name,
            type: file.type,
            base64,
            size: file.size,
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    try {
      const base64Attachments = await Promise.all(
        validFiles.map(convertToBase64)
      );

      const allAttachments = [...attachments, ...base64Attachments];
      const totalSize = allAttachments.reduce(
        (sum, file) => sum + (file as any).size,
        0
      );

      if (totalSize > 10 * 1024 * 1024) {
        setAlertMessage("Total attachments size cannot exceed 10MB.");
        setAlertOpen(true);
        setAlertType("error");
        event.target.value = "";
        return;
      }

      if (allAttachments.length > 5) {
        setAlertMessage("You can attach a maximum of 5 files.");
        setAlertOpen(true);
        setAlertType("error");
        event.target.value = "";
        return;
      }

      setAttachments(allAttachments);
      setAttachmentError("");
    } catch (err) {
      console.error("Error reading files:", err);
      setAlertMessage("Failed to upload some files.");
      setAlertOpen(true);
      setAlertType("error");
    }

    event.target.value = "";
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const addEmails = (
    input: string,
    emails: string[],
    setEmails: React.Dispatch<React.SetStateAction<string[]>>,
    setInput: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const potential = input.trim().split(/\s+/).filter(Boolean);
    const valid: string[] = [];
    const errors: string[] = [];

    // Check max limit
    if (emails.length >= 10) {
      setError("Maximum 10 emails allowed");
      setInput("");
      return;
    }

    potential.forEach((email) => {
      if (!isValidEmail(email)) {
        errors.push(`${email} is invalid`);
      } else if (emails.includes(email)) {
        errors.push(`${email} already added`);
      } else {
        valid.push(email);
      }
    });

    // Only add up to 10
    const availableSlots = 10 - emails.length;
    const toAdd = valid.slice(0, availableSlots);

    if (toAdd.length > 0) {
      setEmails([...emails, ...toAdd]);
      setInput("");
      if (valid.length > availableSlots) {
        errors.push(`Only ${availableSlots} more allowed`);
      }
    }

    if (errors.length > 0) {
      setError(errors.join(", "));
    } else {
      setError("");
    }
  };

  const handleInputChange = (
    val: string,
    emails: string[],
    setEmails: React.Dispatch<React.SetStateAction<string[]>>,
    setInput: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setError("");
    if (val.endsWith(" ")) {
      setInput(val);
      addEmails(val, emails, setEmails, setInput, setError);
    } else {
      setInput(val);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    inputValue: string,
    emails: string[],
    setEmails: React.Dispatch<React.SetStateAction<string[]>>,
    setInputValue: React.Dispatch<React.SetStateAction<string>>,
    setError: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (inputValue.trim() === "") return;

      addEmails(inputValue, emails, setEmails, setInputValue, setError);
    }
  };

  const handleSend = async () => {
    let hasError = false;

    // Validate 'to'
    if (to.length === 0) {
      setToError("At least one recipient is required");
      hasError = true;
    } else {
      setToError("");
    }

    // Validate 'subject'
    if (!subject.trim()) {
      setSubjectError("Subject is required");
      hasError = true;
    } else {
      setSubjectError("");
    }

    // Validate 'message' - check for text content OR images
    const editorState = editorInstance?.getEditorState();
    const empty = editorState ? isLexicalEditorEmpty(editorState) : false;
    const hasImages = message.includes("<img") || message.includes("<image");

    if (!message.trim() || (empty && !hasImages)) {
      setMessageError("Message body is required");
      hasError = true;
    } else {
      setMessageError("");
    }

    if ((message.trim() || (!empty && !hasImages)) && !callValidateMergeTokens(message)) {
      setMessageError('Email body contains invalid or incomplete merge tokens');
      hasError = true;
    }

    if (hasError) return;

    // Process message - it's already in HTML format with images
    const processedMessage = message;

    const payload = {
      to,
      cc,
      bcc,
      subject,
      body: processedMessage,
      attachments: attachments.map((file) => ({
        filename: file.name,
        contentType: file.type,
        content: file.base64,
      })),
      isHTML: true,
      relatedToCustomerId: customerDetails?.id,
      includeOptOutText: templateData?.include_opt_out_text || false,
      templateId: templateData?.id || null,
    };

    setLoading(true);
    try {
      await createEmail(payload);
      handleReset();
      if (onSuccess) onSuccess();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ?? "Failed to send email."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (customerDetails) {
      const result = convertKeysToPascalCase(customerDetails);
      setPlaceHolderValues(result);
    }
  }, [customerDetails]);

  const fetchTemplateDetails = async () => {
    try {
      setLoading?.(true);
      const params = {
        templateType: "Email",
        includeBinaryUrls: true
      }
      const response = await getEmailTemplateDetails(
        templateData?.id,
        params
      );
      setSelectedTemplateData(response?.data?.data);
      if (response?.data?.data?.attachments?.length > 0) {
        console.log('attachments are there', response?.data?.data?.attachments);
        setAttachments(
          response?.data?.data?.attachments?.map((file: any) => ({
            name: file.filename,
            type: file.mimeType,
            base64: file.base64,
          })) || []
        );
      }
      setLoading?.(false);
    } catch (error: any) {
      setLoading?.(false);
      console.error("Error fetching email template details:", error);
    }
  };

  useEffect(() => {
    if (templateData?.id) {
      setSubject(templateData.subject);
      if (templateData?.body) {
        const finalHtml = replaceMergeFields(
          templateData?.body,
          placeHolderValues,
          templateMetaData
        );
        setDefaultHtmlMessage(finalHtml);
        setMessage(finalHtml);
      } else {
        setMessage(templateData?.body || "");
        setDefaultHtmlMessage(templateData?.body || "");
      }
      setAttachments(
        templateData?.attachments?.map((file: any) => ({
          name: file.filename,
          type: file.mimeType,
          base64: file.base64,
        })) || []
      );
    } else {
      setMessage("");
      setDefaultHtmlMessage(templateData?.body || "");
    }
  }, [templateData, customerDetails, open]);

  useEffect(() => {
    if (templateData?.id && open) {
      fetchTemplateDetails();
    }
  }, [open]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  if (!open) return null;

  return (
    <div className="compose-email-dialog">
      <Paper
        elevation={5}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 16,
          maxHeight: { xs: "530px", sm: "540px", lg: "560px" },
          borderRadius: 2,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
        className="compose-email-popup-paper"
      >
        <Box
          sx={{
            backgroundColor: "#fff8e1",
            padding: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight="bold">New Message</Typography>
          <IconButton
            size="small"
            onClick={() => {
              handleReset();
              onClose();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* To + Cc/Bcc on same line */}
          <Box className="custom-autocomplete-container">
            <Box sx={{ mb: 1, width: "99%", position: "relative" }}>
              <Autocomplete
                multiple
                fullWidth
                freeSolo
                options={[]}
                disableClearable
                filterOptions={(options) => options}
                value={to}
                onChange={(_, newValue) => setTo(newValue)}
                inputValue={toInput}
                onInputChange={(_, val) =>
                  handleInputChange(val, to, setTo, setToInput, setToError)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      // key={option}
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      sx={{
                        bgcolor: "#eee",
                        minWidth: "60px",
                        maxWidth: "60px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginRight: "4px",
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="To"
                    variant="standard"
                    margin="dense"
                    error={!!toError}
                    helperText={toError}
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleKeyDown(
                          e,
                          toInput,
                          to,
                          setTo,
                          setToInput,
                          setToError,
                        );
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: false,
                      sx: {
                        pr: 12,
                      },
                    }}
                  />
                )}
              />

              {/* Button Group Wrapper */}
              <Box className="button-group">
                {!showCc && (
                  <Button
                    onClick={() => setShowCc(true)}
                    size="small"
                    className="cc-bcc-button"
                  >
                    Cc
                  </Button>
                )}
                {!showBcc && (
                  <Button
                    onClick={() => setShowBcc(true)}
                    size="small"
                    className="cc-bcc-button"
                  >
                    Bcc
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {showCc && (
            <Box sx={{ position: "relative", mb: 1, width: "100%" }}>
              <Box className="custom-autocomplete-container">
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  freeSolo
                  inputValue={ccInput}
                  filterOptions={(options) => options}
                  onInputChange={(_, val) =>
                    handleInputChange(val, cc, setCc, setCcInput, setCcError)
                  }
                  options={[]}
                  value={cc}
                  onChange={(_, newValue) => setCc(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        sx={{ bgcolor: "#eee" }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="cc"
                      variant="standard"
                      margin="dense"
                      error={!!ccError}
                      helperText={ccError}
                      onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          handleKeyDown(
                            e,
                            ccInput,
                            cc,
                            setCc,
                            setCcInput,
                            setCcError,
                          );
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: false,
                        sx: {
                          pr: 4,
                        },
                      }}
                    />
                  )}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowCc(false);
                    setCc([]);
                    setCcInput("");
                    setCcError("");
                  }}
                  className="close-button"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          {showBcc && (
            <Box sx={{ position: "relative", mb: 1, width: "100%" }}>
              <Box className="custom-autocomplete-container">
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  freeSolo
                  filterOptions={(options) => options}
                  options={[]}
                  inputValue={bccInput}
                  onInputChange={(_, val) =>
                    handleInputChange(
                      val,
                      bcc,
                      setBcc,
                      setBccInput,
                      setBccError,
                    )
                  }
                  value={bcc}
                  onChange={(_, newValue) => setBcc(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        sx={{ bgcolor: "#eee" }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Bcc"
                      variant="standard"
                      margin="dense"
                      error={!!bccError}
                      helperText={bccError}
                      onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          handleKeyDown(
                            e,
                            bccInput,
                            bcc,
                            setBcc,
                            setBccInput,
                            setBccError,
                          );
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: false,
                        sx: {
                          pr: 4,
                        },
                      }}
                    />
                  )}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowBcc(false);
                    setBcc([]);
                    setBccInput("");
                    setBccError("");
                  }}
                  className="close-button"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Subject field */}
          <TextField
            fullWidth
            placeholder="Subject"
            variant="standard"
            margin="dense"
            InputProps={{ disableUnderline: false }}
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              if (subjectError) setSubjectError("");
            }}
            error={!!subjectError}
            helperText={subjectError}
          />

          {/* Message body with QR code button - Rich content editor */}

          <>
            {/* {!templateData?.id ?
            <TextField
              fullWidth
              placeholder="Message"
              multiline
              rows={12}
              variant="standard"
              margin="dense"
              InputProps={{
                disableUnderline: true,
                sx: {
                  border: "none",
                  padding: 0,
                  "& textarea": {
                    border: "none",
                    outline: "none",
                  },
                },
              }}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (messageError) setMessageError("");
              }}
              error={!!messageError}
              helperText={messageError}
            /> :}  */}
            <div className="lexical-editor-container">
              <LexicalEditor
                onEditorInit={(editorInstance) => {
                  setEditorInstance(editorInstance);
                }}
                handleDataChange={(htmlText: any, children: any) => {
                  // const finalHtml = replaceMergeFields(
                  //   htmlText,
                  //   placeHolderValues,
                  //   templateMetaData
                  // );
                  // setMessage(finalHtml);
                  setMessage(htmlText);
                  if (messageError) setMessageError("");
                }}
                defaultHtmlJson={defaulHtmlMessage}
                placeHolderValues={placeHolderValues}
                templateMetaData={templateMetaData}
                replaceMergeFields={true}
                includeOptOutText={templateData?.include_opt_out_text ?? false}
              />
              <div className="error-message email-body-error">
                {messageError}
              </div>
            </div>
          </>
          {attachments.length > 0 && (
            <Box className="attachments-list">
              {attachments.map((file, index) => {
                const dotIndex = file.name.lastIndexOf(".");
                const name =
                  dotIndex !== -1
                    ? file.name.substring(0, dotIndex)
                    : file.name;
                const extension =
                  dotIndex !== -1 ? file.name.substring(dotIndex) : "";

                return (
                  <Box key={index} className="attachment-container">
                    <Box className="attachment-content">
                      {getIconBasedOnFileExt(
                        file.name.split(".").pop()?.toLowerCase() || "",
                      )}
                      <Typography
                        variant="caption"
                        className="attachment-name"
                        title={file.name}
                      >
                        <Box component="span" className="name-part">
                          {name}
                        </Box>
                        <Box component="span" className="extension-part">
                          {extension}
                        </Box>
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAttachment(index)}
                      sx={{ p: "2px" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
          {templateData?.include_opt_out_text &&
            templateMetaData &&
            templateMetaData.length > 0 && (
              <Box className="opt-out-text">
                <Typography variant="body2" className="opt-out-text-label">
                  {
                    templateMetaData.find(
                      (item: any) => item.key === "OptOutText",
                    )?.value
                  }
                </Typography>
              </Box>
            )}

          <Box className="actions-container">
            <Button
              className="attach-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={attachments.length >= 5}
            >
              <img src={AttachmentIcon} alt="Attach" /> Attach Files
            </Button>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xlsx,.csv,.png,.jpg,.jpeg,.txt"
              multiple
              ref={fileInputRef}
              className="hidden-input"
              onChange={handleFileChange}
            />

            <Box className="button-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  handleReset();
                  onClose();
                }}
              >
                Delete
              </button>
              <button
                className="cancel-button save-button"
                onClick={handleSend}
              >
                Send
              </button>
            </Box>
          </Box>
        </Box>
      </Paper>

      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </div>
  );
};

export default ComposeEmailDialog;
